import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material'
import { EventId } from '../../../interfaces/IEvent'
import DialogCloseButton from '../../../components/Buttons/DialogCloseButton'
import React, { useContext, useEffect, useState } from 'react'
import { EbAuthenticatedAppContext } from '@eidsivabredband/app-framework'
import { AssignmentEnums, AssignmentErrorCategoryEnum, IdValuePair } from '../../../interfaces/IAssignmentEnum'
import { Entrepreneur } from '../../../interfaces/IEntrepreneur'
import InputField from '../../../components/InputFields/InputField'
import { Assignment, AssignmentCreateDto, ContactDto, CustomerDto } from '../../../interfaces/IAssignment'
import AutocompleteField from '../../../components/InputFields/AutocompleteField'
import DateTimePickerField from '../../../components/InputFields/DateTimePickerField'
import { enqueueSnackbar } from 'notistack'
import { AddressAutocomplete } from './LocationAutocomplete'
import { ToggleButton, ToggleButtonGroup } from '@eidsivabredband/react-component-ui-package'
import { AddCircleOutlined, Clear, DeleteOutlined, Map as MapIcon, PersonPinCircleOutlined } from '@mui/icons-material'
import UserContext from '../../../context/UserContext'
import CoordinatePickerDialog from './CoordinatePickerDialog'
import { isCoordsWithoutAddressMissingAlias, isValidCoordinatePair, parseCoordinatePair } from './AssignmentValidators'

const WGS84_SRID = 4326

const PINNED_ENTREPRENEURS = ['NetCo']

function sortEntrepreneurs(list: Entrepreneur[]): Entrepreneur[] {
  const isPinned = (e: Entrepreneur) =>
    PINNED_ENTREPRENEURS.some((p) => e.entrepreneurName?.toLowerCase().startsWith(p.toLowerCase()))
  return [...list].sort((a, b) => {
    const aPinned = isPinned(a)
    const bPinned = isPinned(b)
    if (aPinned && !bPinned) return -1
    if (!aPinned && bPinned) return 1
    return (a.entrepreneurName ?? '').localeCompare(b.entrepreneurName ?? '', 'nb')
  })
}

interface CreateAssignmentProps {
  eventId: EventId
  isOpen: boolean
  onClose: () => void
  onChange: () => void
}

interface CustomStep {
  label: string
  content: React.ReactNode
}

enum StepLabelEnum {
  errorCategory = 'Feilkategori',
  entrepreneur = 'Entreprenør',
  customer = 'Kunde',
  contacts = 'Kontakt',
  location = 'Site / Lokasjon',
  description = 'Beskrivelse',
}

enum StepNumberEnum {
  errorCategory = 0,
  entrepreneur = 1,
  location = 2,
  customer = 3,
  contacts = 4,
  description = 5,
}

function CreateOrAddAssignment({ eventId, isOpen, onClose, onChange }: CreateAssignmentProps) {
  const { context } = useContext(EbAuthenticatedAppContext)
  const userContext = useContext(UserContext)

  const emptyCustomer: CustomerDto = {
    name: '',
    phone: '',
    email: '',
    customerNumber: null,
    orgNumber: null,
  }

  const defaultContact: ContactDto = {
    contactRole: 1,
    name: userContext.userInfo.fullName,
    phone: null,
    email: userContext.userInfo.email,
  }

  const defaultContactEOC: ContactDto = {
    contactRole: 1,
    name: 'EOC Eidsiva Digital AS',
    phone: '61288680',
    email: 'EOC@eidsiva.no',
  }

  const emptyContact: ContactDto = {
    contactRole: 1,
    name: '',
    phone: '',
    email: '',
  }

  const [assignmentEnums, setAssignmentEnums] = useState<AssignmentEnums>()
  const [entrepreneurs, setEntrepreneurs] = useState<Entrepreneur[]>()
  const [selectedContact, setSelectedContact] = useState<ContactDto>(emptyContact)
  const [disableCreateButton, setDisableCreateButton] = useState<boolean>(false)
  const [createOrAddMode, setCreateOrAddMode] = useState<'create' | 'add'>('create')
  const [assignmentIdToAdd, setAssignmentIdToAdd] = useState<string>('')
  const [verifiedAssignmentToAdd, setVerifiedAssignmentToAdd] = useState<boolean>(false)
  const [addAssignmentLoading, setAddAssignmentLoading] = useState<boolean>(false)
  const [createAssignmentLoading, setCreateAssignmentLoading] = useState<boolean>(false)

  const [assignmentCreateDto, setAssignmentCreateDto] = useState<AssignmentCreateDto>({
    description: '',
    entrepreneur: null,
    deadline: null,
    invoiceReference: null,
    customer: emptyCustomer,
    isSlaCustomer: false,
    errorCategory: AssignmentErrorCategoryEnum.A_3H,
    contacts: [emptyContact],
    address: {
      alias: '',
      addressId: null,
      streetAddress: null,
      postalCode: null,
      city: null,
      municipality: null,
      propertyUnitNumber: null,
    },
  })

  const initialAssignmentCreateDto = {
    description: '',
    entrepreneur: null,
    deadline: null,
    invoiceReference: null,
    customer: emptyCustomer,
    isSlaCustomer: false,
    errorCategory: AssignmentErrorCategoryEnum.A_3H,
    contacts: [emptyContact],
    address: {
      alias: '',
      addressId: null,
      streetAddress: null,
      postalCode: null,
      city: null,
      municipality: null,
      propertyUnitNumber: null,
    },
  }

  const [activeStep, setActiveStep] = useState<StepNumberEnum>(StepNumberEnum.errorCategory)
  const [mapOpen, setMapOpen] = useState<boolean>(false)
  const [latInput, setLatInput] = useState<string>('')
  const [lonInput, setLonInput] = useState<string>('')

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const handleResetCreateAssignment = () => {
    setAssignmentCreateDto(initialAssignmentCreateDto)
    setActiveStep(StepNumberEnum.errorCategory)
    setSelectedContact(defaultContactEOC)
    setLatInput('')
    setLonInput('')
  }

  const hasMetRequiredFields = (step: StepNumberEnum) => {
    switch (step) {
      case StepNumberEnum.errorCategory:
        return assignmentCreateDto.errorCategory !== 0
      case StepNumberEnum.entrepreneur:
        return assignmentCreateDto.entrepreneur !== null
      case StepNumberEnum.customer:
        return true
      case StepNumberEnum.contacts:
        return (
          assignmentCreateDto.contacts.length > 0 &&
          assignmentCreateDto.contacts.every((c) => c.name !== '' && c.email !== '' && c.contactRole !== 0)
        )
      case StepNumberEnum.location: {
        const lat = assignmentCreateDto.address.latitude
        const lon = assignmentCreateDto.address.longitude
        // Bruker har skrevet noe i lat/lon-feltet som ikke kan parses som tall
        if (latInput.trim() !== '' && (lat === undefined || lat === null || Number.isNaN(lat))) return false
        if (lonInput.trim() !== '' && (lon === undefined || lon === null || Number.isNaN(lon))) return false
        if (!isValidCoordinatePair(lat, lon)) return false
        if (isCoordsWithoutAddressMissingAlias(assignmentCreateDto.address)) return false
        return true
      }
      case StepNumberEnum.description:
        return assignmentCreateDto.description !== ''
      default:
        return false
    }
  }

  const handleGetAssignmentEnums = async () => {
    const response = await context.restApiClient.buildRequest(`/api/assignment/enum`).deserializeResponseToJson<AssignmentEnums>().getAsync()

    if (response.json) {
      setAssignmentEnums(response.json)
    }
  }

  const handleGetEntrepreneurs = async () => {
    const response = await context.restApiClient.buildRequest(`/api/entrepreneur`).deserializeResponseToJson<Entrepreneur[]>().getAsync()

    if (response.json) {
      setEntrepreneurs(sortEntrepreneurs(response.json))
    }
  }

  const handleCreateAssignment = async () => {
    setDisableCreateButton(true)
    setCreateAssignmentLoading(true)

    // Field-service avviser kunde-objekt som er ikke-null men ugyldig (CustomerEntity.ToEntity
    // krever name + phone-eller-email). Send null naar alle kundefelt er tomme.
    const c = assignmentCreateDto.customer
    const customerIsEmpty =
      !c?.name?.trim() && !c?.phone?.trim() && !c?.email?.trim() && !c?.orgNumber?.trim()
    const payload = {
      ...assignmentCreateDto,
      customer: customerIsEmpty ? null : assignmentCreateDto.customer,
    }

    const response = await context.restApiClient
      .buildRequest(`/api/event/${eventId}/assignment/create`)
      .serializeToJsonContent(payload)
      .postAsync()

    if (response.error) {
      enqueueSnackbar('Kunne ikke opprette oppdrag', { variant: 'error' })
      setDisableCreateButton(false)
      setCreateAssignmentLoading(false)
    } else {
      var assignment = JSON.parse(response.response?.content || '{}') as Assignment
      enqueueSnackbar(`Oppdrag ${assignment.id.toUpperCase()} opprettet`, {
        variant: 'success',
      })
      onChange()
      setAssignmentCreateDto(initialAssignmentCreateDto)
      setActiveStep(StepNumberEnum.errorCategory)
      setLatInput('')
      setLonInput('')
      onClose()

      setDisableCreateButton(false)
      setCreateAssignmentLoading(false)
    }
  }

  const handleAddRelatedAssignment = async (assignmentId: string) => {
    setAddAssignmentLoading(true)
    setAssignmentIdToAdd(assignmentId)

    const response = await context.restApiClient
      .buildRequest(`/api/assignment?relatedAssignmentIds=${assignmentId}`)
      .deserializeResponseToJson<Assignment[]>()
      .getAsync()

    if (response.error || (response.json && response.json.length === 0)) {
      enqueueSnackbar('Oppdraget finnes ikke', { variant: 'error' })
      setVerifiedAssignmentToAdd(false)
      setAddAssignmentLoading(false)
      return
    }

    if (response.json) {
      await addRelatedAssignmentIdToEvent(assignmentId)

      setVerifiedAssignmentToAdd(true)
      setAssignmentIdToAdd('')
      setAddAssignmentLoading(false)
      onChange()
      onClose()
    }

    setAddAssignmentLoading(false)
  }

  const addRelatedAssignmentIdToEvent = async (assignmentId: string) => {
    const response = await context.restApiClient
      .buildRequest(`/api/events/${eventId}/assignment?relatedAssignmentId=${assignmentId}&action=add`)
      .putAsync()

    if (response.error) {
      enqueueSnackbar('Kunne ikke legge til oppdraget', { variant: 'error' })
    } else {
      enqueueSnackbar(`Oppdrag ${assignmentId.toUpperCase()} lagt til`, {
        variant: 'success',
      })
    }
  }

  useEffect(() => {
    const getAssignmentEnums = async () => {
      await handleGetAssignmentEnums()
    }

    const getEntrepreneurs = async () => {
      await handleGetEntrepreneurs()
    }

    if (isOpen) {
      getAssignmentEnums()
      getEntrepreneurs()
    }
  }, [isOpen])

  const CreateAssignmentDialogContent = () => {
    return (
      <Box sx={{ width: '100%' }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          {createAssignmentSteps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel>{step.label}</StepLabel>
              <StepContent>{step.content}</StepContent>
            </Step>
          ))}
        </Stepper>
      </Box>
    )
  }

  const CreateAssignmentDialogActions = () => {
    return (
      <Stack
        direction={'row'}
        spacing={1}
        sx={{
          width: '100%',
          justifyContent: 'right'
        }}>
        <Button variant={'outlined'} color={'primary'} disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
          Tilbake
        </Button>
        <Button variant={'outlined'} onClick={handleResetCreateAssignment}>
          Nullstill
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        <Button
          disabled={(!hasMetRequiredFields(activeStep) ? true : false) || disableCreateButton}
          variant={'contained'}
          color={activeStep === createAssignmentSteps.length - 1 ? 'success' : 'primary'}
          onClick={activeStep === createAssignmentSteps.length - 1 ? handleCreateAssignment : handleNext}
        >
          {activeStep === createAssignmentSteps.length - 1 ? 'Opprett' : 'Neste'}
        </Button>
      </Stack>
    );
  }

  const AddAssignmentDialogContent = () => {
    return (
      <TextField
        label=""
        placeholder="Oppdragsnummer (FS...)"
        fullWidth
        value={assignmentIdToAdd}
        onChange={(e) => setAssignmentIdToAdd(e.target.value)}
      />
    )
  }

  const AddAssignmentDialogActions = () => {
    return (
      <Stack
        direction={'row'}
        spacing={1}
        sx={{
          width: '100%',
          justifyContent: 'right'
        }}>
        <Box sx={{ flex: '1 1 auto' }} />
        <Button
          loading={addAssignmentLoading}
          variant={'contained'}
          color={'primary'}
          disabled={!/^FS\d+/i.test(assignmentIdToAdd.trim())}
          onClick={async () => {
            await handleAddRelatedAssignment(assignmentIdToAdd)

            if (verifiedAssignmentToAdd) {
              onClose()
            }
          }}
        >
          Legg til
        </Button>
      </Stack>
    );
  }

  const addressLat = assignmentCreateDto.address.latitude
  const addressLon = assignmentCreateDto.address.longitude
  const hasStreetAddress = !!assignmentCreateDto.address.streetAddress && assignmentCreateDto.address.streetAddress.trim().length > 0
  const coordsLocked = hasStreetAddress
  const aliasRequired = isCoordsWithoutAddressMissingAlias(assignmentCreateDto.address)
  const latInputUnparsed = latInput.trim() !== '' && (addressLat === undefined || addressLat === null || Number.isNaN(addressLat))
  const lonInputUnparsed = lonInput.trim() !== '' && (addressLon === undefined || addressLon === null || Number.isNaN(addressLon))
  const coordPairInvalid = !isValidCoordinatePair(addressLat, addressLon) || latInputUnparsed || lonInputUnparsed

  const setCoords = (nextLat: number | undefined, nextLon: number | undefined, syncInputs = true) => {
    setAssignmentCreateDto({
      ...assignmentCreateDto,
      address: {
        ...assignmentCreateDto.address,
        latitude: nextLat as number,
        longitude: nextLon as number,
        srid: nextLat !== undefined && nextLon !== undefined ? WGS84_SRID : assignmentCreateDto.address.srid,
      },
    })
    if (syncInputs) {
      setLatInput(nextLat !== undefined ? String(nextLat) : '')
      setLonInput(nextLon !== undefined ? String(nextLon) : '')
    }
  }

  const handleLatChange = (raw: string) => {
    setLatInput(raw)
    const trimmed = raw.trim()
    if (!trimmed) {
      setCoords(undefined, addressLon as number | undefined, false)
      return
    }
    const pair = parseCoordinatePair(trimmed)
    if (pair) {
      setCoords(pair.lat, pair.lon, true)
      return
    }
    const parsed = Number(trimmed.replace(',', '.'))
    setCoords(Number.isFinite(parsed) ? parsed : undefined, addressLon as number | undefined, false)
  }

  const handleLonChange = (raw: string) => {
    setLonInput(raw)
    const trimmed = raw.trim()
    if (!trimmed) {
      setCoords(addressLat as number | undefined, undefined, false)
      return
    }
    const pair = parseCoordinatePair(trimmed)
    if (pair) {
      setCoords(pair.lat, pair.lon, true)
      return
    }
    const parsed = Number(trimmed.replace(',', '.'))
    setCoords(addressLat as number | undefined, Number.isFinite(parsed) ? parsed : undefined, false)
  }

  const latLonHelperText = (() => {
    if (coordsLocked) return 'Hentes automatisk fra adressen ved oppretting.'
    if (latInputUnparsed || lonInputUnparsed) return 'Koordinater må være tall.'
    if (coordPairInvalid) return 'Begge koordinater må fylles ut.'
    return 'Bruk hvis adresse mangler.'
  })()

  const locationStepContent = (
    <Stack spacing={2}>
      <Box>
        <InputField
          title="Alias"
          value={assignmentCreateDto.address.alias}
          variant="outlined"
          name="alias"
          onChange={(event) =>
            setAssignmentCreateDto({
              ...assignmentCreateDto,
              address: {
                ...assignmentCreateDto.address,
                alias: event.target.value,
              },
            })
          }
        />
        <Typography variant="caption" color={aliasRequired ? 'error' : 'textPrimary'} sx={{ pl: 1 }}>
          {aliasRequired
            ? 'Alias er påkrevd når oppdraget kun har koordinater (uten adresse). Beskriv hvor oppdraget skal utføres.'
            : 'Alias kan brukes til å beskrive hvor oppdraget skal utføres.'}
        </Typography>
      </Box>
      <AddressAutocomplete
        handleLocationChanged={(address) => {
          if (address) {
            const previewLat = address.latitude ?? null
            const previewLon = address.longitude ?? null
            const previewSrid = address.srid
            setAssignmentCreateDto({
              ...assignmentCreateDto,
              address: {
                alias: assignmentCreateDto.address.alias,
                addressId: address.addressId,
                streetAddress: address.streetAddress,
                postalCode: address.postalCode,
                city: address.city,
                municipality: address.municipality,
                propertyUnitNumber: address.propertyUnitNumber,
                latitude: previewLat,
                longitude: previewLon,
                srid: previewSrid,
              },
            })
            setLatInput(previewLat !== null && previewLat !== undefined ? String(previewLat) : '')
            setLonInput(previewLon !== null && previewLon !== undefined ? String(previewLon) : '')
          } else {
            setAssignmentCreateDto({
              ...assignmentCreateDto,
              address: {
                alias: assignmentCreateDto.address.alias,
                addressId: null,
                streetAddress: null,
                postalCode: null,
                city: null,
                municipality: null,
                propertyUnitNumber: null,
                latitude: assignmentCreateDto.address.latitude,
                longitude: assignmentCreateDto.address.longitude,
                srid: assignmentCreateDto.address.srid,
              },
            })
          }
        }}
        selectedLocation={assignmentCreateDto.address}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" gutterBottom>
            Breddegrad (lat)
          </Typography>
          <TextField
            fullWidth
            value={latInput}
            disabled={coordsLocked}
            error={!coordsLocked && coordPairInvalid}
            helperText={latLonHelperText}
            onChange={(e) => handleLatChange(e.target.value)}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" gutterBottom>
            Lengdegrad (lon)
          </Typography>
          <TextField
            fullWidth
            value={lonInput}
            disabled={coordsLocked}
            error={!coordsLocked && coordPairInvalid}
            helperText={coordsLocked ? '' : latLonHelperText}
            onChange={(e) => handleLonChange(e.target.value)}
          />
        </Box>
        <Button
          variant="outlined"
          startIcon={<MapIcon />}
          onClick={() => setMapOpen(true)}
          disabled={coordsLocked}
          sx={{ alignSelf: 'center', whiteSpace: 'nowrap' }}
        >
          Velg i kart
        </Button>
      </Stack>
      <CoordinatePickerDialog
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        onConfirm={({ lat: pickedLat, lon: pickedLon }) => setCoords(pickedLat, pickedLon)}
        initialLat={addressLat}
        initialLon={addressLon}
      />
    </Stack>
  )

  const createAssignmentSteps: CustomStep[] = [
    {
      label: StepLabelEnum.errorCategory,
      content: assignmentEnums && (
        <Stack spacing={1}>
          <AutocompleteField<IdValuePair>
            title="Feilkategori *"
            options={assignmentEnums.errorCategory || []}
            value={assignmentEnums.errorCategory.find((category) => category.id === assignmentCreateDto.errorCategory)}
            getOptionLabel={(option) => option.value}
            isOptionEqualToValue={(option, value) => option === value}
            placeholder="Velg feilkategori"
            name="assignmentType"
            onChange={(value) => {
              setAssignmentCreateDto({
                ...assignmentCreateDto,
                errorCategory: value?.id || 0,
              })

              if (value?.id === AssignmentErrorCategoryEnum.A_3H) {
                setAssignmentCreateDto({
                  ...assignmentCreateDto,
                  errorCategory: value?.id || 0,
                  deadline: null,
                })
              }
            }}
          />
          {assignmentCreateDto.errorCategory === AssignmentErrorCategoryEnum.A_3H && (
            <Typography variant="caption" sx={{
              pl: 1
            }}>
              Entreprenør vil bli informert om at kritisk oppdrag fra EOC er opprettet (epost).
            </Typography>
          )}
          {assignmentCreateDto.errorCategory !== AssignmentErrorCategoryEnum.A_3H &&
            assignmentCreateDto.errorCategory !== AssignmentErrorCategoryEnum.Unset && (
              <Box>
                <DateTimePickerField
                  title="Tidsfrist"
                  name="deadline"
                  placeholder="Velg tidsfrist (valgfritt)"
                  value={assignmentCreateDto.deadline}
                  onChange={(value) =>
                    setAssignmentCreateDto({
                      ...assignmentCreateDto,
                      deadline: value,
                    })
                  }
                />
                <Typography variant="caption" sx={{
                  pl: 1
                }}>
                  Tidspunkt baserer seg på feilkategori, men kan overstyres her
                </Typography>
              </Box>
            )}
        </Stack>
      ),
    },
    {
      label: StepLabelEnum.entrepreneur,
      content: entrepreneurs && (
        <AutocompleteField<Entrepreneur>
          options={entrepreneurs}
          value={assignmentCreateDto.entrepreneur}
          getOptionLabel={(option) => option?.entrepreneurName || ''}
          isOptionEqualToValue={(option, value) => option.orgNumber === value?.orgNumber}
          placeholder="Velg entreprenør"
          name="entrepreneur"
          onChange={(value) => {
            if (value) {
              setAssignmentCreateDto({
                ...assignmentCreateDto,
                entrepreneur: {
                  entrepreneurName: value?.entrepreneurName || null,
                  orgNumber: value?.orgNumber || null,
                },
              })
            } else {
              setAssignmentCreateDto({
                ...assignmentCreateDto,
                entrepreneur: null,
              })
            }
          }}
        />
      ),
    },
    {
      label: StepLabelEnum.location,
      content: locationStepContent,
    },
    {
      label: StepLabelEnum.customer,
      content: assignmentCreateDto.customer && (
        <Stack spacing={1}>
          <>
            <InputField
              title="Kundenavn"
              value={assignmentCreateDto.customer?.name ?? ''}
              variant="outlined"
              name="customerName"
              onChange={(event) =>
                setAssignmentCreateDto({
                  ...assignmentCreateDto,
                  customer: {
                    ...(assignmentCreateDto.customer ?? emptyCustomer),
                    name: event.target.value,
                  },
                })
              }
            />
            <InputField
              title="Telefonnummer"
              value={assignmentCreateDto.customer?.phone ?? ''}
              variant="outlined"
              name="customerPhone"
              onChange={(event) =>
                setAssignmentCreateDto({
                  ...assignmentCreateDto,
                  customer: {
                    ...(assignmentCreateDto.customer ?? emptyCustomer),
                    phone: event.target.value,
                  },
                })
              }
            />
            <InputField
              title="Epostadresse"
              value={assignmentCreateDto.customer?.email ?? ''}
              variant="outlined"
              name="customerEmail"
              onChange={(event) =>
                setAssignmentCreateDto({
                  ...assignmentCreateDto,
                  customer: {
                    ...(assignmentCreateDto.customer ?? emptyCustomer),
                    email: event.target.value,
                  },
                })
              }
            />
            <InputField
              title="Organisasjonsnummer"
              value={assignmentCreateDto.customer?.orgNumber ?? ''}
              variant="outlined"
              name="customerOrgNumber"
              onChange={(event) =>
                setAssignmentCreateDto({
                  ...assignmentCreateDto,
                  customer: {
                    ...(assignmentCreateDto.customer ?? emptyCustomer),
                    orgNumber: event.target.value,
                  },
                })
              }
            />
          </>
        </Stack>
      ),
    },
    {
      label: StepLabelEnum.contacts,
      content: assignmentCreateDto.contacts.length > 0 && (
        <Stack spacing={2}>
          {assignmentCreateDto.contacts.map((contact, index) => {
            const updateContactField = (partial: Partial<ContactDto>) => {
              setAssignmentCreateDto({
                ...assignmentCreateDto,
                contacts: assignmentCreateDto.contacts.map((c, i) => (i === index ? { ...c, ...partial } : c)),
              })
            }
            const replaceFirstContact = (replacement: ContactDto) => {
              setSelectedContact(replacement)
              setAssignmentCreateDto({
                ...assignmentCreateDto,
                contacts: assignmentCreateDto.contacts.map((c, i) => (i === 0 ? replacement : c)),
              })
            }
            return (
              <Stack
                key={index}
                spacing={1}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                }}
              >
                <Stack
                  direction="row"
                  sx={{ alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <Typography variant="subtitle2">Kontaktperson {index + 1}</Typography>
                  {assignmentCreateDto.contacts.length > 1 && (
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteOutlined />}
                      onClick={() =>
                        setAssignmentCreateDto({
                          ...assignmentCreateDto,
                          contacts: assignmentCreateDto.contacts.filter((_, i) => i !== index),
                        })
                      }
                    >
                      Fjern
                    </Button>
                  )}
                </Stack>
                {assignmentEnums && (
                  <AutocompleteField<IdValuePair>
                    disabled={index === 0}
                    options={assignmentEnums.contactRole}
                    value={assignmentEnums.contactRole.find((role) => role.id === contact.contactRole)}
                    getOptionLabel={(option) => option.value}
                    isOptionEqualToValue={(option, value) => option === value}
                    placeholder="Velg kontaktrolle"
                    name="contactRole"
                    onChange={(value) => updateContactField({ contactRole: value?.id || 0 })}
                  />
                )}
                {index === 0 && (
                  <Box sx={{ alignSelf: 'center' }}>
                    <ToggleButtonGroup>
                      <ToggleButton
                        icon={<Clear />}
                        onClick={() => replaceFirstContact(emptyContact)}
                        selected={selectedContact?.name === emptyContact.name}
                        name="Nullstill"
                      />
                      <ToggleButton
                        icon={<PersonPinCircleOutlined />}
                        onClick={() => replaceFirstContact(defaultContactEOC)}
                        selected={selectedContact?.name === defaultContactEOC.name}
                        name={'EOC'}
                      />
                      <ToggleButton
                        icon={<PersonPinCircleOutlined />}
                        onClick={() => replaceFirstContact(defaultContact)}
                        selected={selectedContact?.name === defaultContact.name}
                        name={userContext.userInfo.fullName}
                      />
                    </ToggleButtonGroup>
                  </Box>
                )}
                <InputField
                  title="Navn *"
                  value={contact.name}
                  variant="outlined"
                  name={`contactName-${index}`}
                  onChange={(event) => updateContactField({ name: event.target.value })}
                />
                <InputField
                  title="Epostadresse *"
                  value={contact.email}
                  variant="outlined"
                  name={`contactEmail-${index}`}
                  onChange={(event) => updateContactField({ email: event.target.value })}
                />
                <InputField
                  title="Telefonnummer"
                  value={contact.phone || ''}
                  variant="outlined"
                  name={`contactPhone-${index}`}
                  onChange={(event) => updateContactField({ phone: event.target.value })}
                />
              </Stack>
            )
          })}
          <Button
            variant="outlined"
            startIcon={<AddCircleOutlined />}
            onClick={() =>
              setAssignmentCreateDto({
                ...assignmentCreateDto,
                contacts: [...assignmentCreateDto.contacts, emptyContact],
              })
            }
            sx={{ alignSelf: 'flex-start' }}
          >
            Legg til kontaktperson
          </Button>
        </Stack>
      ),
    },
    {
      label: StepLabelEnum.description,
      content: (
        <Stack spacing={2}>
          <InputField
            title="Beskrivelse av oppdraget *"
            value={assignmentCreateDto.description}
            variant="outlined"
            name="description"
            rows={16}
            multiline
            onChange={(event) =>
              setAssignmentCreateDto({
                ...assignmentCreateDto,
                description: event.target.value,
              })
            }
          />
        </Stack>
      ),
    },
  ]

  return (
    <Dialog open={isOpen} fullWidth onClose={onClose} scroll={'paper'}>
      <DialogTitle>
        <Stack direction={'row'} spacing={2} sx={{
          alignItems: 'center'
        }}>
          <Button
            disabled={addAssignmentLoading}
            variant={createOrAddMode === 'create' ? 'contained' : 'outlined'}
            onClick={() => setCreateOrAddMode('create')}
          >
            {'Opprett oppdrag'}
          </Button>
          <Button
            disabled={createAssignmentLoading}
            variant={createOrAddMode === 'add' ? 'contained' : 'outlined'}
            onClick={() => setCreateOrAddMode('add')}
          >
            {'Legg til oppdrag'}
          </Button>
        </Stack>
        <DialogCloseButton handleClose={onClose} disabled={addAssignmentLoading || createAssignmentLoading} />
      </DialogTitle>
      <DialogContent dividers>
        {createOrAddMode === 'create' && CreateAssignmentDialogContent()}
        {createOrAddMode === 'add' && AddAssignmentDialogContent()}
      </DialogContent>
      <DialogActions>
        {createOrAddMode === 'create' && CreateAssignmentDialogActions()}
        {createOrAddMode === 'add' && AddAssignmentDialogActions()}
      </DialogActions>
    </Dialog>
  );
}

export default CreateOrAddAssignment
